import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOnceMutation } from '@vnetwork/margarita-shell/once-mutation';
import { Button } from '@vnetwork/ui/primitives';
import { Alert, Loading } from '@vnetwork/ui/feedback';
import { AuthShell } from './components/AuthShell';
import { MigrationOtpForm } from './components/MigrationOtpForm';
import { MigrationSetPasswordForm } from './components/MigrationSetPasswordForm';
import { useAuth } from '@/auth/AuthProvider';
import {
  resolveReconciliation,
  sendMigrationOtp,
  type ReconciliationInvoice,
} from '@/api/auth-flow';
import { startSsoLogin } from '@/lib/sso';
import { brand } from '@/config/brand';
import { paths } from '@/lib/paths';

// Where the customer lands once authenticated — the CDN usage / reconciliation view.
const DEST = paths.usage.base;

function InvoiceSummary({ invoice }: { invoice: ReconciliationInvoice }) {
  const rows: [string, string][] = [
    ['Invoice', invoice.invoice_code || `#${invoice.invoice_id}`],
    ['Amount', `${invoice.total.toLocaleString()} ${invoice.currency}`],
    ['Period', [invoice.apply_start, invoice.apply_end].filter(Boolean).join(' → ')],
  ];
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--vn-space-2)',
        padding: 'var(--vn-space-4)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-subtle)',
        fontSize: 'var(--vn-font-size-md)',
      }}
    >
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-4)' }}>
          <span style={{ color: 'var(--vn-fg-muted)' }}>{label}</span>
          <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Reconciliation deep-link landing page. Public and self-authenticating: the
 * `?token=` from the email is resolved via @mgmt, then the customer is branched:
 *   - already on SSO   → sign in
 *   - has a local login → migrate (OTP → set password)
 *   - no account yet    → onboard (OTP → set password + contact details)
 * All branches converge on SSO sign-in landing at /services/cdn/usage.
 */
export default function ReconciliationPage() {
  const { isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  // Capture the token once, then strip it from the URL so it never lingers in
  // history / gets shared. It lives only in this component's memory afterwards.
  const [token] = useState(() => params.get('token') ?? '');
  const [migrationToken, setMigrationToken] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    if (token) window.history.replaceState({}, '', '/auth/reconciliation');
  }, [token]);

  const resolve = useQuery({
    queryKey: ['reconciliation-resolve'],
    queryFn: () => resolveReconciliation(token),
    // Already-signed-in customers skip the whole onboarding: they can view it
    // directly (redirect below).
    enabled: Boolean(token) && !isAuthenticated,
    retry: false,
  });

  const otpSend = useOnceMutation({ mutationFn: sendMigrationOtp });
  const data = resolve.data;

  // Onboarding/migrate branch: send the first OTP once the email is resolved.
  useEffect(() => {
    if (data && !data.has_sso && !otpSent) {
      setOtpSent(true);
      otpSend.mutate(data.email);
    }
  }, [data, otpSent, otpSend]);

  const invalidLink = (
    <AuthShell title="Link unavailable" subtitle="This reconciliation link can't be opened.">
      <Alert tone="danger" title="Invalid or expired link">
        This link is invalid or has expired (valid for 48 hours). Please use the
        latest reconciliation email, or contact us at {brand.emails.care}.
      </Alert>
    </AuthShell>
  );

  // Already signed in → go straight to the reconciliation/usage view.
  if (isAuthenticated) return <Navigate to={DEST} replace />;

  if (!token) return invalidLink;
  if (resolve.isLoading) {
    return (
      <AuthShell title="Opening your reconciliation" subtitle="Please wait a moment.">
        <Loading />
      </AuthShell>
    );
  }
  if (resolve.isError || !data) return invalidLink;

  // Already has an SSO account → just sign in.
  if (data.has_sso) {
    return (
      <AuthShell
        title="View your reconciliation"
        subtitle={`Sign in with ${brand.ssoName} to view the details.`}
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <InvoiceSummary invoice={data.invoice} />
          <Button type="button" variant="primary" fullWidth onClick={() => startSsoLogin(DEST)}>
            Sign in to view
          </Button>
        </div>
      </AuthShell>
    );
  }

  // OTP verified → set the password (and contact details for onboarding).
  if (migrationToken) {
    return (
      <AuthShell
        title="Set your password"
        subtitle="Create your login and confirm your contact details."
      >
        <MigrationSetPasswordForm
          email={data.email}
          migrationToken={migrationToken}
          from={DEST}
          collectContact
          customerId={data.customer_id}
          prefillName={data.prefill?.name}
          prefillPhone={data.prefill?.phone}
        />
      </AuthShell>
    );
  }

  // Verify email ownership via the OTP we just sent.
  return (
    <AuthShell
      title="Verify your email"
      subtitle="Enter the code we sent so you can view your reconciliation."
    >
      <InvoiceSummary invoice={data.invoice} />
      <div style={{ height: 'var(--vn-space-4)' }} />
      <MigrationOtpForm email={data.email} onVerified={(t) => setMigrationToken(t)} />
    </AuthShell>
  );
}
